import {Injectable} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {
  ActiveCartFacade,
  CreateCartFailEvent,
  CreateCartSuccessEvent,
  DeleteCartFailEvent,
  DeleteCartSuccessEvent,
  MultiCartFacade,
} from '@spartacus/cart/base/root';
import {EventService, UserIdService} from '@spartacus/core';
import {Observable, combineLatest, merge} from 'rxjs';
import {first, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CartService {
  constructor(
    protected activeCartFacade: ActiveCartFacade,
    protected multiCartFacade: MultiCartFacade,
    protected userIdService: UserIdService,
    protected eventService: EventService,
  ) {}

  /**
   * Loads the active cart from the backend and places it in the rxjs store of spartacus.
   */
  public reloadActiveCart(): void {
    combineLatest([this.activeCartFacade.getActiveCartId(), this.userIdService.getUserId()])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([cId, uid]) => {
        this.multiCartFacade.loadCart({
          userId: uid,
          cartId: cId,
          extraData: {
            active: true,
          },
        });
      });
  }

  deleteActiveCart(): Observable<boolean> {
    return this.activeCartFacade.getActiveCartId().pipe(
      withLatestFrom(this.userIdService.getUserId()),
      take(1),
      tap(([cartId, userId]) => {
        this.multiCartFacade.deleteCart(cartId, userId);
      }),
      switchMap(() =>
        merge(
          this.eventService.get(DeleteCartSuccessEvent).pipe(map(() => true)),
          this.eventService.get(DeleteCartFailEvent).pipe(map(() => false)),
        ).pipe(take(1)),
      ),
    );
  }

  createCart(): Observable<boolean> {
    return this.userIdService.getUserId().pipe(
      take(1),
      tap((userId: string) =>
        this.multiCartFacade.createCart({
          userId,
          extraData: {
            active: true,
          },
        }),
      ),
      switchMap(() =>
        merge(
          this.eventService.get(CreateCartSuccessEvent).pipe(map(() => true)),
          this.eventService.get(CreateCartFailEvent).pipe(map(() => false)),
        ).pipe(take(1)),
      ),
    );
  }
}
